import React from "react";
import { Linking, ScrollView, Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { FreeplayEntry, FreeplayEvent, FreeplayLeague } from "@freeplay/shared-code";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { TrophyIcon } from "../components/icons/TrophyIcon";
import { GlobalStyles } from "../components/style/Style";
import { MediaQueries } from "../components/hooks/dynamicStyleHook";
import { useServices } from "../contexts/ServiceContext";
import { GameTitle } from "../components/picks/GameTitle";
import { IndividualBespokePromptDisplay } from "../components/overview/IndividualBespokePromptDisplay";
import { ButtonSize, ButtonTheme, RoundedButton } from "../components/buttons/roundedButton/RoundedButton";
import DefaultImage from "../assets/default_background.png";
import DefaultTSImage from "../assets/default_background_ts.png";
export interface PicksOverviewProps {
    event: FreeplayEvent;
    entry?: FreeplayEntry;
}

export const BespokePickOverviewView: React.FC<PicksOverviewProps> = ({ event, entry }) => {
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(generateStyles);
    const { bespokeSelections } = entry ?? { bespokeSelections: undefined };

    const ContainerView = edgeDeploy ? View : ScrollView;
    const optional = event.prizeDescription || "Make your choices and press submit!";

    const buttonCheck = () => {
        if (event.platformEvents.length <= 0 && event.buttonText && event.buttonUrl && event.buttonUrl !== "") {
            return true;
        }
        return false;
    };

    return (
        <View style={[edgeDeploy ? styles.edgeContainer : styles.container]}>
            <GameTitle
                title={event.name}
                date={format(parseISO(event.bettingEnds), "MMMM dd, yyyy")}
                backgroundImage={event.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage}
                optionalText={optional}
            />

            <ContainerView style={styles.selectionContainer}>
                <View style={[styles.choiceContainer, edgeDeploy ? styles.edgeChoiceContainer : {}]}>
                    {event.bespokePrompts.map((prompt, index: number) => {
                        return (
                            <IndividualBespokePromptDisplay
                                key={`bespoke-outcome-${index}`}
                                prompt={prompt}
                                selection={bespokeSelections ? bespokeSelections[index] : undefined}
                            />
                        );
                    })}
                </View>
            </ContainerView>
            <View style={styles.infoContainer}>
                <View style={styles.scoreContainer}>
                    <TrophyIcon fill={EStyleSheet.value(GlobalStyles.TROPHY)} />
                    <Text key={"Optional"} style={[styles.score, edgeDeploy ? styles.edgeScore : {}]}>
                        You can score up to {event.bespokePrompts.length} point
                        {event.bespokePrompts.length === 1 ? "" : "s"}
                    </Text>
                </View>
            </View>
            {buttonCheck() && (
                <View style={styles.ctaContainer}>
                    <RoundedButton
                        config={{
                            buttonText: event.buttonText,
                            size: ButtonSize.LARGE,
                            width: edgeDeploy ? 300 : 250,
                            height: edgeDeploy ? 60 : 50,
                            theme: ButtonTheme.CTA,
                            margin: { bottom: 0 },
                        }}
                        testID={`next-game-button`}
                        onPress={() => Linking.openURL(event.buttonUrl)}
                    />
                </View>
            )}
        </View>
    );
};

const generateStyles = () =>
    EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
            edgeMiddleButton: {
                marginHorizontal: "10px",
            },
            edgeChoiceContainer: {
                paddingHorizontal: 0,
            },
        },
        [MediaQueries.XSMALL]: {
            edgeScore: {
                fontSize: 18,
            },
        },
        edgeContainer: {
            width: "100%",
            maxWidth: 860,
            marginHorizontal: "auto",
            padding: 30,
            marginVertical: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            flex: 1,
            alignItems: "center",
        },
        container: {
            flex: 1,
            alignItems: "center",
        },
        infoContainer: {
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.5,
            shadowRadius: 16,
            shadowColor: () => EStyleSheet.value(GlobalStyles.DEFAULT_SHADOW),
            width: "100%",
            height: 70,
            paddingVertical: 10,
            paddingHorizontal: 10,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.GAME_CTA_BACKGROUND_COLOUR),
        },
        title: {
            fontSize: 32,
            lineHeight: 32,
            margin: 0,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.ACCENT_COLOUR),
        },
        subTitle: {
            fontSize: 14,
            lineHeight: 14,
            fontWeight: "700",
            marginBottom: 5,
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
        },
        selectionContainer: {
            flex: 1,
            flexDirection: "column",
            alignSelf: "stretch",
        },
        ctaContainer: {
            marginTop: "auto",
            width: "100%",
            height: 70,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.SUBMITTED_CTA_BACKGROUND),
        },
        choiceContainer: {
            justifyContent: "center",
            paddingHorizontal: 20,
        },
        edgeChoiceContainer: {
            paddingHorizontal: 20,
        },
        scoreContainer: {
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
        },
        score: {
            fontSize: 14,
            paddingLeft: 5,
            textAlign: "center",
            fontWeight: "700",
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
        },
        edgeScore: {
            fontSize: 24,
        },
    });
